// safety/incidents-and-injuries/components/tabs/InvestigationTab.tsx
import React, { useState } from 'react';
import { FileText, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { SectionTitle, AddButton, FormModal } from '../shared';
import { fmtDate, mergedInvestigation, correctiveActionStatusClass, uid } from '../../utils/incidents-utils';
import { useAuth } from '../../../../../context/AuthContext';
import type {
    Incident,
    IncidentInvestigation,
    EmployeeRef,
    CorrectiveActionStatus,
} from '../../../../../types/incident.types';

const ACTION_STATUSES: CorrectiveActionStatus[] = ['Open', 'In Progress', 'Done'];

export const InvestigationTab: React.FC<{
    incident: Incident;
    employees: EmployeeRef[];
    // Text edits go through onChange (parent debounces the save);
    // structural changes (notes, actions, Yes/No) go through onCommit (saves immediately).
    onChange: (investigation: IncidentInvestigation) => void;
    onCommit: (investigation: IncidentInvestigation) => void;
}> = ({ incident: r, employees, onChange, onCommit }) => {
    const { currentUser } = useAuth();
    const [noteModal, setNoteModal] = useState(false);
    const [actionModal, setActionModal] = useState(false);
    const inv = mergedInvestigation(r);

    // Note log sorted newest → oldest (full ISO timestamps keep same-day order stable)
    const sortedNotes = [...inv.notes].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

    const setActionStatus = (id: string, status: CorrectiveActionStatus) =>
        onCommit({ ...inv, correctiveActions: inv.correctiveActions.map((a) => (a.id === id ? { ...a, status } : a)) });

    return (
        <>
            {/* ---------- Investigation note log ---------- */}
            <SectionTitle title="Investigation Notes" right={<AddButton label="Add Note" onClick={() => setNoteModal(true)} />} />
            <div className="space-y-3 mb-6">
                {sortedNotes.length === 0 && <p className="text-sm text-muted-foreground">No investigation notes yet.</p>}
                {sortedNotes.map((n) => (
                    <div key={n.id} className="rounded-lg border border-border p-4">
                        <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center text-primary flex-shrink-0">
                                <FileText size={16} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-medium">{n.submittedBy}</div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground">{fmtDate(n.date)}</span>
                                        <button
                                            onClick={() => onCommit({ ...inv, notes: inv.notes.filter((x) => x.id !== n.id) })}
                                            className="text-muted-foreground hover:text-destructive"
                                            title="Delete note"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                                <p className="text-sm text-foreground mt-2 whitespace-pre-wrap">{n.body}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ---------- Breached Procedures ---------- */}
            <SectionTitle title="Breached Procedures" />
            <textarea
                rows={3}
                value={inv.breachedProcedures}
                onChange={(e) => onChange({ ...inv, breachedProcedures: e.target.value })}
                placeholder="List any procedures that were breached..."
                className="w-full rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm mb-6"
            />

            {/* ---------- Risk Assessment Reviewed ---------- */}
            <SectionTitle title="Risk Assessment Reviewed" />
            <div className="flex gap-2 mb-6">
                {[true, false].map((b) => (
                    <button
                        key={String(b)}
                        type="button"
                        onClick={() => onCommit({ ...inv, riskAssessmentReviewed: b })}
                        className={`h-10 px-5 rounded-lg text-sm font-medium border ${
                            inv.riskAssessmentReviewed === b
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'border-border hover:bg-accent'
                        }`}
                    >
                        {b ? 'Yes' : 'No'}
                    </button>
                ))}
            </div>

            {/* ---------- Corrective Actions ---------- */}
            <SectionTitle title="Corrective Actions" right={<AddButton label="Add Action" onClick={() => setActionModal(true)} />} />
            <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/30 border-b border-border">
                        <tr>
                            {['Recommended Action', 'Responsible Person', 'Due Date', 'Status', ''].map((h) => (
                                <th key={h} className="text-left font-medium text-[11px] uppercase tracking-wide text-muted-foreground px-3 py-2.5">
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {inv.correctiveActions.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-3 py-8 text-center text-sm text-muted-foreground">
                                    No corrective actions yet.
                                </td>
                            </tr>
                        )}
                        {inv.correctiveActions.map((a) => (
                            <tr key={a.id} className="border-b border-border last:border-0">
                                <td className="px-3 py-3 whitespace-pre-wrap">{a.action}</td>
                                <td className="px-3 py-3 text-muted-foreground">{a.responsible}</td>
                                <td className="px-3 py-3 text-muted-foreground">{fmtDate(a.dueDate)}</td>
                                <td className="px-3 py-3">
                                    <select
                                        value={a.status}
                                        onChange={(e) => setActionStatus(a.id, e.target.value as CorrectiveActionStatus)}
                                        className={`h-7 rounded-lg px-2 text-xs font-medium ${correctiveActionStatusClass(a.status)}`}
                                    >
                                        {ACTION_STATUSES.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </td>
                                <td className="px-3 py-3 text-right">
                                    <button
                                        onClick={() => onCommit({ ...inv, correctiveActions: inv.correctiveActions.filter((x) => x.id !== a.id) })}
                                        className="text-muted-foreground hover:text-destructive"
                                        title="Delete action"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {noteModal && (
                <FormModal
                    title="Add Investigation Note"
                    fields={[{ key: 'body', label: 'Note', type: 'textarea', value: '' }]}
                    onClose={() => setNoteModal(false)}
                    onSubmit={(v) => {
                        if (!v.body?.trim()) {
                            toast.error('Note is required');
                            return;
                        }
                        onCommit({
                            ...inv,
                            notes: [
                                {
                                    id: uid('invnote'),
                                    body: v.body.trim(),
                                    submittedBy: currentUser?.name ?? 'Unknown',
                                    date: new Date().toISOString(),
                                },
                                ...inv.notes,
                            ],
                        });
                        setNoteModal(false);
                    }}
                />
            )}

            {actionModal && (
                <FormModal
                    title="Add Corrective Action"
                    fields={[
                        { key: 'action', label: 'Recommended Action', type: 'textarea', value: '' },
                        {
                            key: 'responsible',
                            label: 'Responsible Person',
                            type: employees.length > 0 ? 'select' : 'text',
                            options: employees.map((e) => e.name),
                            value: employees[0]?.name ?? '',
                        },
                        { key: 'dueDate', label: 'Due Date', type: 'date', value: '' },
                        { key: 'status', label: 'Status', type: 'select', options: ACTION_STATUSES, value: 'Open' },
                    ]}
                    onClose={() => setActionModal(false)}
                    onSubmit={(v) => {
                        if (!v.action?.trim()) {
                            toast.error('Recommended action is required');
                            return;
                        }
                        if (!v.responsible) {
                            toast.error('Responsible person is required');
                            return;
                        }
                        onCommit({
                            ...inv,
                            correctiveActions: [
                                {
                                    id: uid('action'),
                                    action: v.action.trim(),
                                    responsible: v.responsible,
                                    dueDate: v.dueDate,
                                    status: v.status as CorrectiveActionStatus,
                                },
                                ...inv.correctiveActions,
                            ],
                        });
                        setActionModal(false);
                    }}
                />
            )}
        </>
    );
};
